/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormGroup, ReactiveFormsModule, FormsModule } from "@angular/forms";
import { SelectModule } from "primeng/select";
import { InputNumberModule } from "primeng/inputnumber";
import { DatePickerModule } from "primeng/datepicker";
import { AccordionModule } from "primeng/accordion";
import { InputSwitchModule } from "primeng/inputswitch";
import { ButtonModule } from "primeng/button";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { Subject, takeUntil } from "rxjs";
import { Site } from "../../../../../core/models/site.interface";
import { AdvancedConfirmationDialogComponent } from "../../advanced-confirmation-dialog/advanced-confirmation-dialog.component";
import { ConfirmationDialogData } from "../../../interfaces/confirmation-dialog-data.interface";
import { ChangeFrequencyDialogComponent } from "../../change-frequency-dialog/change-frequency-dialog.component";
import { ScheduledOrdersHelper } from "../../../helpers/scheduled-orders.helper";
import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../../core/services/qpilot.service";
import { SessionService } from "../../../../../core/services/session.service";
import { ScheduledOrderForm } from "../../../interfaces/scheduled-order-form.interface";
import { TimezoneHelper } from "../../../../../core/utils/timezone.helper";

/**
 * Frequency editor component for managing order recurrence settings
 * Used in scheduled order create/edit forms to configure frequency
 * COMPACT REDESIGN - Follows minimalist UI principles
 */
@Component({
  selector: "app-frequency-editor",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SelectModule,
    InputNumberModule,
    DatePickerModule,
    AccordionModule,
    InputSwitchModule,
    ButtonModule,
  ],
  providers: [DialogService],
  templateUrl: "./frequency-editor.component.html",
  styleUrl: "./frequency-editor.component.scss",
})
export class FrequencyEditorComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() formGroup!: FormGroup<ScheduledOrderForm>;
  @Input() site?: Site;
  @Input() disabled = false;
  @Input() required = false;
  @Input() advancedOptionsAllowed = true; // Subscription tier restriction
  @Input() tierName = ""; // Name of current subscription tier
  @Input() upgradeUrl = ""; // URL to upgrade subscription
  @Input() accessToken?: QPilotAccessToken; // Optional token for embedded mode
  @Output() upgradeRequested = new EventEmitter<void>(); // Emitted when upgrade dialog should be shown

  // State for advanced options toggle
  showAdvancedOptions = false;

  // PrimeNG Accordion active index (both panels open by default)
  // Accordion - track which panels are open (value="0" = Frequency Settings)
  // Panel open by default - uses string array matching panel's value attribute
  // In PrimeNG v19, [multiple]="true" requires [(value)] with string[]
  scheduleAccordionActiveValue: string[] = ["0"];

  // Legacy accordion section state (kept for backward compatibility but not used with PrimeNG)
  frequencySectionExpanded = true; // Open by default
  advancedSectionExpanded = false; // Closed by default

  statusOptions = [
    { label: "Active", value: "Active" },
    { label: "Paused", value: "Paused" },
    { label: "Failed", value: "Failed" },
  ];

  frequencyTypes = [
    { label: "Days", value: "Days" },
    { label: "Weeks", value: "Weeks" },
    { label: "Months", value: "Months" },
    { label: "Day of the Week", value: "DayOfTheWeek" },
    { label: "Day of the Month", value: "DayOfTheMonth" },
  ];

  // Timezone offset options with descriptive names (UTC-12 to UTC+14)
  timezoneOptions = [
    { label: "(GMT-12:00) International Date Line West", value: -12 },
    { label: "(GMT-11:00) Coordinated Universal Time-11", value: -11 },
    { label: "(GMT-10:00) Hawaii", value: -10 },
    { label: "(GMT-09:00) Alaska", value: -9 },
    { label: "(GMT-08:00) Pacific Time (US & Canada)", value: -8 },
    { label: "(GMT-07:00) Mountain Time (US & Canada)", value: -7 },
    { label: "(GMT-06:00) Central Time (US & Canada)", value: -6 },
    { label: "(GMT-05:00) Eastern Time (US & Canada)", value: -5 },
    { label: "(GMT-04:00) Atlantic Time (Canada)", value: -4 },
    { label: "(GMT-03:00) Brasilia, Buenos Aires, Greenland", value: -3 },
    { label: "(GMT-02:00) Coordinated Universal Time-02", value: -2 },
    { label: "(GMT-01:00) Azores, Cape Verde Islands", value: -1 },
    { label: "(GMT+00:00) London, Dublin, Lisbon", value: 0 },
    { label: "(GMT+01:00) Central European Time", value: 1 },
    { label: "(GMT+02:00) Eastern European Time", value: 2 },
    { label: "(GMT+03:00) Moscow, Istanbul, Riyadh", value: 3 },
    { label: "(GMT+04:00) Abu Dhabi, Muscat, Baku", value: 4 },
    { label: "(GMT+05:00) Islamabad, Karachi, Tashkent", value: 5 },
    { label: "(GMT+06:00) Dhaka, Almaty", value: 6 },
    { label: "(GMT+07:00) Bangkok, Hanoi, Jakarta", value: 7 },
    { label: "(GMT+08:00) Beijing, Singapore, Perth", value: 8 },
    { label: "(GMT+09:00) Tokyo, Seoul, Osaka", value: 9 },
    { label: "(GMT+10:00) Sydney, Melbourne, Brisbane", value: 10 },
    { label: "(GMT+11:00) Solomon Islands, New Caledonia", value: 11 },
    { label: "(GMT+12:00) Auckland, Wellington, Fiji", value: 12 },
    { label: "(GMT+13:00) Nuku'alofa, Samoa", value: 13 },
    { label: "(GMT+14:00) Kiritimati Island", value: 14 },
  ];

  private dialogRef?: DynamicDialogRef;

  constructor(
    private dialogService: DialogService,
    private scheduledOrdersHelper: ScheduledOrdersHelper,
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private timezoneHelper: TimezoneHelper,
  ) {}

  ngOnInit(): void {
    // Set defaults if not provided
    if (!this.formGroup.controls.frequencyType.value) {
      this.formGroup.controls.frequencyType.setValue("Months");
    }
    if (!this.formGroup.controls.frequency.value) {
      this.formGroup.controls.frequency.setValue(1);
    }
    if (
      this.formGroup.controls.cycles.value === undefined ||
      this.formGroup.controls.cycles.value === null
    ) {
      this.formGroup.controls.cycles.setValue(0); // 0 = infinite
    }
    if (!this.formGroup.controls.status.value) {
      this.formGroup.controls.status.setValue("Paused"); // Default to Paused for new orders
    }
    if (
      this.formGroup.controls.nextOccurrenceOffset.value === undefined ||
      this.formGroup.controls.nextOccurrenceOffset.value === null
    ) {
      this.formGroup.controls.nextOccurrenceOffset.setValue(0);
    }
    // Initialize timezone offset from site if not set
    if (
      (this.formGroup.controls.utcOffset.value === undefined ||
        this.formGroup.controls.utcOffset.value === null) &&
      this.site?.UtcOffset !== undefined
    ) {
      this.formGroup.controls.utcOffset.setValue(this.site.UtcOffset);
    }
  }

  /**
   * Handle frequency type change
   */
  onFrequencyTypeChange(): void {
    // Reset frequency to 1 when type changes
    this.formGroup.controls.frequency.setValue(1);
  }

  /**
   * Handle frequency value change
   * @param value New frequency value
   */
  onFrequencyChange(value: number | null): void {
    if (value === null || value < 1) {
      this.formGroup.controls.frequency.setValue(1);
    } else {
      this.formGroup.controls.frequency.setValue(value);
    }
  }

  /**
   * Handle cycles blur (when user finishes editing)
   * IMPORTANT: Cycles DOES trigger totals recalculation (matches old QMC line 238 - blur event)
   */
  onCyclesBlur(): void {
    const cycles = this.formGroup.controls.cycles.value;
    // Normalize value
    if (cycles === null || cycles === undefined || cycles < 0) {
      this.formGroup.controls.cycles.setValue(0);
    }
    // Form automatically triggers parent change detection - no need to emit
  }

  /**
   * Handle next occurrence date change
   * IMPORTANT: Date changes trigger totals (old QMC line 169), but NOT time changes
   * @param date New date value
   */
  onNextOccurrenceChange(date: Date | null): void {
    if (date) {
      this.formGroup.controls.nextOccurrenceUtc.setValue(date);
      // Form automatically triggers parent change detection
    }
  }

  /**
   * Get descriptive label for current frequency
   * @returns Human-readable frequency description
   */
  getFrequencyLabel(): string {
    const frequencyType = this.formGroup.controls.frequencyType.value;
    const frequency = this.formGroup.controls.frequency.value;

    if (!frequencyType || !frequency) {
      return "Not configured";
    }

    return this.scheduledOrdersHelper.getFrequencyDisplayName(
      frequencyType,
      frequency,
    );
  }

  /**
   * Get descriptive label for cycles
   * @returns Human-readable cycles description
   */
  getCyclesLabel(): string {
    const cycles = this.formGroup.controls.cycles.value;

    if (cycles === undefined || cycles === null || cycles === 0) {
      return "Recurring indefinitely";
    }
    return `Will process ${cycles} time${cycles > 1 ? "s" : ""}`;
  }

  /**
   * Handle status change
   * IMPORTANT: Status does NOT trigger totals recalculation (old QMC line 149 has no event handler)
   */
  onStatusChange(): void {
    // Don't call emitChange() - status changes don't affect totals
    // Just update the value via two-way binding
  }

  /**
   * Handle next occurrence offset blur (when user finishes editing)
   * IMPORTANT: NextOccurrenceOffset does NOT trigger totals (old QMC line 226 has no event handler)
   * We only normalize the value on blur, but don't trigger totals recalculation
   */
  onNextOccurrenceOffsetBlur(): void {
    const offset = this.formGroup.controls.nextOccurrenceOffset.value;
    // Normalize value
    if (offset === null || offset === undefined || offset < 0) {
      this.formGroup.controls.nextOccurrenceOffset.setValue(0);
    }
    // Form automatically handles value changes - no need to emit
  }

  /**
   * Handle switch toggle (when user clicks the switch directly)
   * @param newValue The new value from the switch
   */
  onSwitchToggle(newValue: boolean): void {
    // If trying to enable advanced options but tier doesn't allow it
    if (newValue && !this.advancedOptionsAllowed) {
      this.showUpgradeDialog();
      return;
    }

    // Update the value
    this.showAdvancedOptions = newValue;
  }

  /**
   * Handle label click (when user clicks the label text)
   */
  onLabelClick(): void {
    const newValue = !this.showAdvancedOptions;
    this.onSwitchToggle(newValue);
  }

  /**
   * Toggle advanced options visibility
   * If tier doesn't allow advanced options, show upgrade dialog
   * @deprecated Use onSwitchToggle or onLabelClick instead
   */
  toggleAdvancedOptions(): void {
    this.onLabelClick();
  }

  /**
   * Show upgrade dialog when advanced options are not allowed
   */
  private showUpgradeDialog(): void {
    const tierDisplay = this.tierName || "Current Plan";
    const upgradeLink = this.upgradeUrl
      ? `<a href="${this.upgradeUrl}" target="_blank" rel="noopener noreferrer">upgrade your subscription</a>`
      : "upgrade your subscription";

    const dialogData: ConfirmationDialogData = {
      title: "Upgrade to use Advanced Options",
      message: `Your ${tierDisplay} does not have access to Advanced Options.<br/><br/>To enable Advanced Options, please ${upgradeLink}.`,
      okButtonText: this.upgradeUrl ? "UPGRADE NOW" : "OK",
      showCancelButton: false,
      severity: "info",
      icon: "pi pi-lock",
    };

    this.dialogRef = this.dialogService.open(
      AdvancedConfirmationDialogComponent,
      {
        header: dialogData.title,
        data: dialogData,
        width: "500px",
        modal: true,
        dismissableMask: true,
      },
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.dialogRef.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        // If user clicked "UPGRADE NOW" and we have an upgrade URL, open it
        if (confirmed && this.upgradeUrl) {
          window.open(this.upgradeUrl, "_blank", "noopener,noreferrer");
        }
      });
  }

  /**
   * Handle timezone offset change
   * IMPORTANT: Timezone does NOT trigger totals recalculation (old QMC line 210 has no event handler)
   */
  onTimezoneOffsetChange(): void {
    // Don't call emitChange() - timezone is metadata, doesn't affect totals calculation
  }

  /**
   * Get formatted UTC offset for display (matches old QMC format)
   * @param utcOffset Numeric offset (e.g., -5, 0, 8)
   * @returns Formatted string (e.g., "GMT-5 UTC-05", "UTC", "GMT+8 UTC+08")
   */
  /**
   * Format UTC offset for display in labels
   * Matches old QMC getFormattedUtcOffset (lines 592-603)
   * Returns simple format: "UTC", "UTC+05", "UTC-05"
   */
  getFormattedUtcOffset(utcOffset: number | undefined): string {
    if (utcOffset === undefined || utcOffset === null || utcOffset === 0) {
      return "UTC";
    }

    // Match old QMC format exactly: "UTC+05" or "UTC-05"
    const absOffset = Math.abs(utcOffset).toString().padStart(2, "0");
    if (utcOffset >= 0) {
      return `UTC+${absOffset}`;
    } else {
      return `UTC-${absOffset}`;
    }
  }

  /**
   * Check if customer timezone differs from site timezone
   * @returns True if customer has a different timezone than site
   */
  hasCustomerTimezone(): boolean {
    const utcOffset = this.formGroup.controls.utcOffset.value;

    return (
      utcOffset !== undefined &&
      utcOffset !== null &&
      this.site?.UtcOffset !== undefined &&
      utcOffset !== this.site.UtcOffset
    );
  }

  /**
   * Calculate customer's next occurrence date/time with their timezone
   * Matches old QMC getCustomerNextOccurrence (lines 549-568)
   * IMPORTANT: Old QMC builds date with SITE timezone, then converts to customer timezone in template pipe
   * @returns Date object adjusted for customer's timezone, or undefined if not enough data
   */
  getCustomerNextOccurrence(): Date | undefined {
    const nextOccurrenceUtc = this.formGroup.controls.nextOccurrenceUtc.value;
    const customerUtcOffset = this.formGroup.controls.utcOffset.value;

    if (
      !nextOccurrenceUtc ||
      !this.site?.UtcOffset ||
      customerUtcOffset === undefined ||
      customerUtcOffset === null
    ) {
      return undefined;
    }

    const date = new Date(nextOccurrenceUtc);

    // Extract time as string (HH:mm) - matches old QMC which receives time as string parameter
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const time = `${hours}:${minutes}`;

    // IMPORTANT: Use SITE timezone to build the date (matches old QMC line 352)
    // Old QMC: getCustomerNextOccurrence(date, time, site.UtcOffset)
    const siteUtcOffset = this.site.UtcOffset;
    const absOffset = Math.abs(siteUtcOffset).toString().padStart(2, "0");
    const timezone =
      siteUtcOffset >= 0 ? `+${absOffset}:00` : `-${absOffset}:00`;

    // Extract date components
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    // Build ISO string with SITE timezone (matches old QMC line 564)
    const isoString = `${year}-${month}-${day}T${time}:00.000${timezone}`;
    const siteTimezoneDate = new Date(Date.parse(isoString));

    // Now convert from site timezone to customer timezone (matches old QMC dateTimeFormat pipe)
    // Apply timezone difference
    const timezoneDiff = customerUtcOffset - siteUtcOffset;
    const customerDate = new Date(
      siteTimezoneDate.getTime() + timezoneDiff * 60 * 60 * 1000,
    );

    return customerDate;
  }

  /**
   * Calculate when the order will actually process (with offset applied)
   * Matches old QMC getProcessingTime (lines 570-590)
   * IMPORTANT: Uses SITE timezone for both creation and display (matches old QMC line 373)
   * @returns Date object for processing time, or undefined if not enough data
   */
  getProcessingTime(): Date | undefined {
    const nextOccurrenceUtc = this.formGroup.controls.nextOccurrenceUtc.value;
    const nextOccurrenceOffset =
      this.formGroup.controls.nextOccurrenceOffset.value;

    if (
      !nextOccurrenceUtc ||
      !this.site?.UtcOffset ||
      nextOccurrenceOffset === undefined ||
      nextOccurrenceOffset === null
    ) {
      return undefined;
    }

    const date = new Date(nextOccurrenceUtc);

    // Apply the day offset (subtract days before next occurrence)
    const shiftedDate = new Date(date);
    shiftedDate.setDate(shiftedDate.getDate() - nextOccurrenceOffset);

    // Extract time as string (HH:mm)
    const hours = shiftedDate.getHours().toString().padStart(2, "0");
    const minutes = shiftedDate.getMinutes().toString().padStart(2, "0");
    const time = `${hours}:${minutes}`;

    // IMPORTANT: Use SITE timezone (matches old QMC line 373)
    // Old QMC: getProcessingTime(date, time, site.UtcOffset, offset)
    const siteUtcOffset = this.site.UtcOffset;
    const absOffset = Math.abs(siteUtcOffset).toString().padStart(2, "0");
    const timezone =
      siteUtcOffset >= 0 ? `+${absOffset}:00` : `-${absOffset}:00`;

    // Extract date components
    const year = shiftedDate.getFullYear();
    const month = (shiftedDate.getMonth() + 1).toString().padStart(2, "0");
    const day = shiftedDate.getDate().toString().padStart(2, "0");

    // Build ISO string with SITE timezone (matches old QMC line 587)
    const isoString = `${year}-${month}-${day}T${time}:00.000${timezone}`;
    const processingTime = new Date(Date.parse(isoString));

    return processingTime;
  }

  /**
   * Open change frequency dialog
   * Allows user to change frequency type and value
   */
  editFrequency(): void {
    if (this.disabled) {
      return;
    }

    this.dialogRef = this.dialogService.open(ChangeFrequencyDialogComponent, {
      header: "Change Frequency",
      data: {
        frequency: this.formGroup.controls.frequency.value,
        frequencyType: this.formGroup.controls.frequencyType.value,
      },
      width: "600px",
      modal: true,
      dismissableMask: true,
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.dialogRef.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: { frequency: number; frequencyType: string } | undefined) => {
          if (result && result.frequency && result.frequencyType) {
            this.formGroup.controls.frequencyType.setValue(
              result.frequencyType,
            );
            this.formGroup.controls.frequency.setValue(result.frequency);

            // Recalculate next occurrence date based on new frequency
            this.fetchNextOccurrenceUtc();
          }
        },
      );
  }

  /**
   * Fetch next occurrence UTC from API based on current frequency settings
   * Called after frequency is changed to recalculate the next occurrence date
   */
  private fetchNextOccurrenceUtc(): void {
    const frequencyType = this.formGroup.controls.frequencyType.value;
    const frequency = this.formGroup.controls.frequency.value;

    const token = this.accessToken ?? this.sessionService.accessToken;
    if (!this.site?.Id || !token || !frequencyType || !frequency) {
      return;
    }

    // IMPORTANT: Old QMC always calculates from TODAY when changing frequency
    // (not from current next occurrence). Matches old QMC line 1691.
    // Create a ScheduledOrderListItem object for the API call
    const scheduledOrderForApi: any = {
      Id: 0, // New orders don't have ID yet
      Frequency: frequency,
      FrequencyType: frequencyType,
      NextOccurrenceUtc: null,
      CustomerFirstName: "",
      CustomerLastName: "",
      CustomerEmail: "",
      Status: this.formGroup.controls.status.value || "Paused",
      UtcOffset: this.formGroup.controls.utcOffset.value || 0,
      Cycles: this.formGroup.controls.cycles.value || 0,
      CurrencyIso: this.formGroup.controls.currencyIso.value || "USD",
      TotalProductQuantity: 0,
      IsSelected: false,
    };

    // Always use getScheduledOrderNextOccurrence (calculates from TODAY)
    // This matches old QMC behavior (dashboard-scheduled-orders-update.component.ts line 715)
    this.qpilotService
      .getScheduledOrderNextOccurrence(
        scheduledOrderForApi,
        this.site.Id,
        token,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (nextOccurrence: Date) => {
          // Ensure the response is a valid Date object
          if (nextOccurrence) {
            // Convert from UTC to site timezone for display (matches old QMC getDateFromUtcOffsetForDisplay)
            const convertedDate = this.timezoneHelper.convertToTimezone(
              new Date(nextOccurrence),
              this.site?.UtcOffset || 0,
            );
            this.formGroup.controls.nextOccurrenceUtc.setValue(convertedDate);
          }
        },
        error: (error) => {
          console.error("Error fetching next occurrence:", error);
        },
      });
  }

  /**
   * Get unit label for frequency display
   * @returns Unit label (e.g., "days", "weeks", "months")
   */
  getUnitLabel(): string {
    const frequencyType = this.formGroup.controls.frequencyType.value;

    if (!frequencyType) {
      return "";
    }

    const frequency = this.formGroup.controls.frequency.value || 1;

    switch (frequencyType) {
      case "Days":
        return frequency === 1 ? "day" : "days";
      case "Weeks":
        return frequency === 1 ? "week" : "weeks";
      case "Months":
        return frequency === 1 ? "month" : "months";
      case "DayOfTheWeek":
        return "day of week";
      case "DayOfTheMonth":
        return "day of month";
      default:
        return "";
    }
  }

  /**
   * Check if any advanced options are configured
   * @returns True if advanced options are set
   */
  hasAdvancedOptions(): boolean {
    const utcOffset = this.formGroup.controls.utcOffset.value;
    const nextOccurrenceOffset =
      this.formGroup.controls.nextOccurrenceOffset.value;

    return !!(
      (utcOffset !== undefined && utcOffset !== null && utcOffset !== 0) ||
      (nextOccurrenceOffset && nextOccurrenceOffset > 0)
    );
  }

  /**
   * Get count of configured advanced options
   * @returns Number of advanced options set
   */
  getAdvancedCount(): number {
    const utcOffset = this.formGroup.controls.utcOffset.value;
    const nextOccurrenceOffset =
      this.formGroup.controls.nextOccurrenceOffset.value;

    let count = 0;
    if (utcOffset !== undefined && utcOffset !== null && utcOffset !== 0) {
      count++;
    }
    if (nextOccurrenceOffset && nextOccurrenceOffset > 0) {
      count++;
    }
    return count;
  }

  /**
   * Get human-readable frequency summary
   * @returns Summary string (e.g., "Every 30 days starting Dec 25, 2025")
   */
  getFrequencySummary(): string {
    const frequencyType = this.formGroup.controls.frequencyType.value;
    const frequency = this.formGroup.controls.frequency.value;

    if (!frequencyType || !frequency) {
      return "Not configured";
    }

    const frequencyLabel = this.getFrequencyLabel();
    const nextOccurrenceUtc = this.formGroup.controls.nextOccurrenceUtc.value;
    const nextDate = nextOccurrenceUtc
      ? new Date(nextOccurrenceUtc).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "Not set";

    return `${frequencyLabel} starting ${nextDate}`;
  }

  /**
   * Toggle accordion section visibility
   * @param section Section to toggle ('frequency' or 'advanced')
   */
  toggleSection(section: "frequency" | "advanced"): void {
    if (section === "frequency") {
      this.frequencySectionExpanded = !this.frequencySectionExpanded;
    } else if (section === "advanced") {
      this.advancedSectionExpanded = !this.advancedSectionExpanded;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
